import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFunds, getFundManagers, createFund } from '../api';
import { useMobile } from '../components/Layout';
import { useTheme } from '../ThemeContext';
import {
  Building2, Plus, X, Search, ChevronRight, FileText,
} from 'lucide-react';

const FUND_TYPES = [
  { value: 'UCITS', label: 'UCITS' }, { value: 'AIF', label: 'AIF' },
  { value: 'ELTIF', label: 'ELTIF' }, { value: 'RAIF', label: 'RAIF' },
  { value: 'SIF', label: 'SIF' }, { value: 'SICAR', label: 'SICAR' },
];

const INVESTOR_TYPES = [
  { value: 'RETAIL', label: 'Retail' },
  { value: 'PROFESSIONAL', label: 'Professional' },
  { value: 'BOTH', label: 'Both' },
];

const LEGAL_FORMS = [
  { value: 'SICAV', label: 'SICAV' }, { value: 'FCP', label: 'FCP' },
  { value: 'SCA', label: 'SCA' }, { value: 'SCS', label: 'SCS' },
  { value: 'SCSp', label: 'SCSp' },
];

const CURRENCIES = ['EUR','USD','GBP','CHF','JPY','SEK','NOK','DKK','SGD','HKD','CAD','AUD','NZD'];
const SRI_LABELS = ['Very Low','Low','Medium-Low','Medium','Medium-High','High','Very High'];

const Badge = ({ children, theme }) => (
  <span style={{
    background: theme.bgInput,
    color: theme.textSecondary,
    border: `1px solid ${theme.border}`,
    padding: '3px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  }}>
    {children}
  </span>
);

const Label = ({ children, required, theme }) => (
  <label style={{
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: theme.textSecondary,
    marginBottom: 6,
  }}>
    {children} {required && <span style={{ color: '#991B1B' }}>*</span>}
  </label>
);

const Funds = () => {
  const navigate = useNavigate();
  const isMobile = useMobile();
  const { theme } = useTheme();

  const [funds, setFunds] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const emptyForm = {
    fund_name: '',
    fund_manager_id: '',
    fund_type: 'UCITS',
    target_investors: 'RETAIL',
    isin: '',
    currency: 'EUR',
    legal_form: 'SICAV',
    aum: '',
    risk_indicator: '',
    minimum_holding_years: '',
    redemption_window: 'NONE',
  };

  const [form, setForm] = useState(emptyForm);

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: `1px solid ${theme.border}`,
    fontSize: 13,
    color: theme.textPrimary,
    background: theme.bgInput,
    boxSizing: 'border-box',
    outline: 'none',
  };

  const primaryBtn = {
    background: '#2563EB',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    padding: '9px 15px',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
  };

  const secondaryBtn = {
    background: theme.bgInput,
    color: theme.textSecondary,
    border: `1px solid ${theme.border}`,
    borderRadius: 8,
    padding: '9px 15px',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
  };

  useEffect(() => {
    Promise.all([getFunds(), getFundManagers()])
      .then(([fundRes, managerRes]) => {
        setFunds(fundRes.data || []);
        setManagers(managerRes.data || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = funds.filter(fund => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      fund.fund_name?.toLowerCase().includes(q) ||
      fund.fund_manager_name?.toLowerCase().includes(q) ||
      fund.isin?.toLowerCase().includes(q) ||
      fund.fund_type?.toLowerCase().includes(q)
    );
  });

  const totalAum = funds.reduce((sum, f) => sum + Number(f.aum || 0), 0);
  const activeCurrencies = new Set(funds.map(f => f.currency).filter(Boolean)).size;
  const fundTypes = new Set(funds.map(f => f.fund_type).filter(Boolean)).size;

  const formatAum = (value) => {
    const n = Number(value || 0);
    if (!n) return '-';
    if (n >= 1000000000) return `€${(n / 1000000000).toFixed(1)}B`;
    if (n >= 1000000) return `€${(n / 1000000).toFixed(1)}M`;
    return `€${n.toLocaleString('en-LU')}`;
  };

  const resetModal = () => {
    setShowModal(false);
    setForm(emptyForm);
    setCreateError(null);
  };

  const handleCreate = async () => {
    if (!form.fund_name || !form.fund_manager_id) {
      setCreateError('Fund name and fund manager are required.');
      return;
    }

    if (form.isin && !/^[A-Z]{2}[A-Z0-9]{10}$/.test(form.isin)) {
      setCreateError('ISIN must be 12 characters: 2 letters + 10 alphanumeric.');
      return;
    }

    setCreating(true);
    setCreateError(null);

    try {
      await createFund(form);
      const res = await getFunds();
      setFunds(res.data || []);
      resetModal();
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to create fund.');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: theme.textTertiary, fontSize: 14 }}>Loading funds...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
        marginBottom: 24,
        flexWrap: 'wrap',
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: theme.textPrimary }}>
            Funds
          </h1>
          <p style={{ margin: '5px 0 0', fontSize: 13, color: theme.textSecondary }}>
            Fund register, structure, and portfolio-level operating data.
          </p>
        </div>

        <button onClick={() => { setShowModal(true); setCreateError(null); }} style={primaryBtn}>
          <Plus size={15} /> Add Fund
        </button>
      </div>

      <div style={{
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        boxShadow: theme.shadowSm,
        marginBottom: 16,
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
          borderBottom: `1px solid ${theme.border}`,
        }}>
          {[
            ['Total funds', funds.length],
            ['Total AUM', formatAum(totalAum)],
            ['Fund types', fundTypes],
            ['Currencies', activeCurrencies],
          ].map(([label, value], idx) => (
            <div key={label} style={{
              padding: '16px 18px',
              borderRight: !isMobile && idx < 3 ? `1px solid ${theme.border}` : 'none',
            }}>
              <p style={{ margin: 0, fontSize: 12, color: theme.textTertiary, fontWeight: 600 }}>{label}</p>
              <p style={{ margin: '6px 0 0', fontSize: 24, fontWeight: 700, color: theme.textPrimary, lineHeight: 1 }}>
                {value}
              </p>
            </div>
          ))}
        </div>

        <div style={{
          padding: '14px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}>
          <div style={{ position: 'relative', width: isMobile ? '100%' : 340 }}>
            <Search size={14} color={theme.textTertiary} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              placeholder="Search funds, managers, ISIN"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ ...inputStyle, paddingLeft: 34 }}
            />
          </div>

          <span style={{ fontSize: 12, color: theme.textTertiary }}>
            Showing {filtered.length} of {funds.length}
          </span>
        </div>
      </div>

      <div style={{
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        boxShadow: theme.shadowSm,
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '14px 16px',
          borderBottom: `1px solid ${theme.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          alignItems: 'center',
        }}>
          <div>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: theme.textPrimary }}>
              Fund Register
            </p>
            <p style={{ margin: '3px 0 0', fontSize: 12, color: theme.textTertiary }}>
              Registered funds and structural metadata
            </p>
          </div>
          <Building2 size={17} color={theme.textTertiary} />
        </div>

        {filtered.length === 0 ? (
          <div style={{ padding: '56px 24px', textAlign: 'center' }}>
            <Building2 size={34} color={theme.textTertiary} style={{ marginBottom: 10 }} />
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: theme.textPrimary }}>
              No funds found
            </p>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: theme.textSecondary }}>
              Create a fund or adjust your search.
            </p>
          </div>
        ) : isMobile ? (
          <div style={{ display: 'grid', gap: 8, padding: 10 }}>
            {filtered.map(fund => (
              <button key={fund.id} onClick={() => navigate(`/funds/${fund.id}`)} style={{
                textAlign: 'left',
                background: theme.bgInput,
                border: `1px solid ${theme.border}`,
                borderRadius: 10,
                padding: 13,
                cursor: 'pointer',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: theme.textPrimary }}>
                      {fund.fund_name}
                    </p>
                    <p style={{ margin: '3px 0 0', fontSize: 11, color: theme.textSecondary }}>
                      {fund.fund_manager_name || '-'}
                    </p>
                  </div>
                  <Badge theme={theme}>{fund.fund_type || '-'}</Badge>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 12 }}>
                  <div>
                    <p style={{ margin: 0, fontSize: 10, color: theme.textTertiary }}>AUM</p>
                    <p style={{ margin: '3px 0 0', fontSize: 12, color: theme.textPrimary, fontWeight: 600 }}>{formatAum(fund.aum)}</p>
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: 10, color: theme.textTertiary }}>SRI</p>
                    <p style={{ margin: '3px 0 0', fontSize: 12, color: theme.textPrimary, fontWeight: 600 }}>{fund.risk_indicator ? `${fund.risk_indicator}/7` : '-'}</p>
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: 10, color: theme.textTertiary }}>Currency</p>
                    <p style={{ margin: '3px 0 0', fontSize: 12, color: theme.textPrimary, fontWeight: 600 }}>{fund.currency || '-'}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: theme.bgBase, borderBottom: `1px solid ${theme.border}` }}>
                {['Fund', 'Type', 'Legal Form', 'AUM', 'SRI', 'Currency', ''].map(h => (
                  <th key={h} style={{
                    padding: '11px 16px',
                    textAlign: 'left',
                    fontSize: 10,
                    fontWeight: 600,
                    color: theme.textTertiary,
                    textTransform: 'uppercase',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((fund, idx) => (
                <tr key={fund.id} onClick={() => navigate(`/funds/${fund.id}`)}
                  style={{
                    borderBottom: idx < filtered.length - 1 ? `1px solid ${theme.border}` : 'none',
                    cursor: 'pointer',
                    transition: 'background 0.12s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = theme.bgCardHover}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: '#F8FAFC',
                        border: '1px solid #E2E8F0',
                        color: '#334155',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <FileText size={14} />
                      </div>
                      <div>
                        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: theme.textPrimary }}>
                          {fund.fund_name}
                        </p>
                        <p style={{ margin: '2px 0 0', fontSize: 11, color: theme.textTertiary }}>
                          {fund.fund_manager_name || '-'}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '14px 16px' }}><Badge theme={theme}>{fund.fund_type || '-'}</Badge></td>
                  <td style={{ padding: '14px 16px', fontSize: 12, color: theme.textSecondary }}>{fund.legal_form || '-'}</td>
                  <td style={{ padding: '14px 16px', fontSize: 12, color: theme.textSecondary }}>{formatAum(fund.aum)}</td>
                  <td style={{ padding: '14px 16px', fontSize: 12, color: theme.textSecondary }}>{fund.risk_indicator ? `${fund.risk_indicator}/7` : '-'}</td>
                  <td style={{ padding: '14px 16px', fontSize: 12, color: theme.textSecondary }}>{fund.currency || '-'}</td>
                  <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                    <ChevronRight size={15} color={theme.textTertiary} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: theme.bgOverlay,
          display: 'flex',
          alignItems: isMobile ? 'flex-end' : 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: theme.bgCard,
            borderRadius: isMobile ? '18px 18px 0 0' : 12,
            width: '100%',
            maxWidth: isMobile ? '100%' : 560,
            maxHeight: isMobile ? '92vh' : '90vh',
            overflowY: 'auto',
            boxShadow: theme.shadowLg,
            border: isMobile ? 'none' : `1px solid ${theme.border}`,
          }}>
            <div style={{
              padding: 20,
              borderBottom: `1px solid ${theme.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'sticky',
              top: 0,
              background: theme.bgCard,
              zIndex: 1,
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: theme.textPrimary }}>
                  Add fund
                </h2>
                <p style={{ margin: '3px 0 0', fontSize: 12, color: theme.textSecondary }}>
                  Register a fund under a fund manager.
                </p>
              </div>
              <button onClick={resetModal} style={{
                background: theme.bgInput,
                border: `1px solid ${theme.border}`,
                borderRadius: 8,
                cursor: 'pointer',
                padding: 8,
                display: 'flex',
                color: theme.textSecondary,
              }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: 20 }}>
              <div style={{ marginBottom: 14 }}>
                <Label required theme={theme}>Fund Name</Label>
                <input type="text" value={form.fund_name}
                  onChange={e => setForm(d => ({ ...d, fund_name: e.target.value }))}
                  placeholder="Luxe Capital UCITS Fund" style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <Label required theme={theme}>Fund Manager</Label>
                <select value={form.fund_manager_id}
                  onChange={e => setForm(d => ({ ...d, fund_manager_id: e.target.value }))}
                  style={{ ...inputStyle, cursor: 'pointer' }}
                >
                  <option value="">Select a fund manager</option>
                  {managers.map(m => <option key={m.id} value={m.id}>{m.legal_name}</option>)}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <Label theme={theme}>Fund Type</Label>
                  <select value={form.fund_type} onChange={e => setForm(d => ({ ...d, fund_type: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
                    {FUND_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label theme={theme}>Legal Form</Label>
                  <select value={form.legal_form} onChange={e => setForm(d => ({ ...d, legal_form: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
                    {LEGAL_FORMS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <Label theme={theme}>Target Investors</Label>
                  <select value={form.target_investors} onChange={e => setForm(d => ({ ...d, target_investors: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
                    {INVESTOR_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label theme={theme}>Currency</Label>
                  <select value={form.currency} onChange={e => setForm(d => ({ ...d, currency: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
                    {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <Label theme={theme}>Min. Holding Years</Label>
                  <input type="number" value={form.minimum_holding_years}
                    onChange={e => setForm(d => ({ ...d, minimum_holding_years: e.target.value }))}
                    placeholder="e.g. 5" style={inputStyle} min={0}
                  />
                </div>
                <div>
                  <Label theme={theme}>Redemption Window</Label>
                  <select value={form.redemption_window} onChange={e => setForm(d => ({ ...d, redemption_window: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="NONE">None</option>
                    <option value="QUARTERLY">Quarterly</option>
                    <option value="SEMI_ANNUAL">Semi-Annual</option>
                    <option value="ANNUAL">Annual</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <Label theme={theme}>ISIN</Label>
                  <input type="text" value={form.isin}
                    onChange={e => setForm(d => ({ ...d, isin: e.target.value.toUpperCase() }))}
                    placeholder="LU0000000000" style={inputStyle} maxLength={12}
                  />
                  {form.isin && !/^[A-Z]{2}[A-Z0-9]{10}$/.test(form.isin) && (
                    <p style={{ margin: '4px 0 0', fontSize: 11, color: '#92400E' }}>
                      2 letters + 10 alphanumeric.
                    </p>
                  )}
                </div>
                <div>
                  <Label theme={theme}>AUM</Label>
                  <input type="number" value={form.aum}
                    onChange={e => setForm(d => ({ ...d, aum: e.target.value }))}
                    placeholder="e.g. 50000000" style={inputStyle}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 18 }}>
                <Label theme={theme}>Risk Indicator</Label>
                <select value={form.risk_indicator}
                  onChange={e => setForm(d => ({ ...d, risk_indicator: e.target.value }))}
                  style={{ ...inputStyle, cursor: 'pointer' }}
                >
                  <option value="">Not set</option>
                  {[1,2,3,4,5,6,7].map(n => (
                    <option key={n} value={n}>{n} - {SRI_LABELS[n - 1]}</option>
                  ))}
                </select>
              </div>

              {createError && (
                <div style={{
                  background: '#FEF2F2',
                  borderRadius: 8,
                  padding: '10px 13px',
                  marginBottom: 14,
                  fontSize: 12,
                  color: '#991B1B',
                  fontWeight: 600,
                  border: '1px solid #FECACA',
                }}>
                  {createError}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={resetModal} style={secondaryBtn}>
                  Cancel
                </button>
                <button onClick={handleCreate} disabled={creating} style={{
                  ...primaryBtn,
                  background: creating ? '#93C5FD' : '#2563EB',
                  cursor: creating ? 'not-allowed' : 'pointer',
                }}>
                  {creating ? 'Creating...' : 'Create Fund'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Funds;
